// ============================================================
// CineSync — OpenSubtitles Service (Client Proxy Client)
// ============================================================

import type { SubtitleSearchResult, SavedSubtitle } from '@/types';
import { parseVTT } from '@/services/subtitleService';
import { parseSRT } from '@/lib/utils';
import { nanoid } from 'nanoid';
import { getApiBaseUrl } from '@/lib/apiConfig';

export interface SearchParams {
  query: string;
  language?: string;
  year?: number | string;
}

/**
 * Searches for subtitles via the CineSync backend OpenSubtitles proxy.
 * Keeps API keys and tokens strictly on the backend.
 */
export async function searchSubtitles({
  query,
  language = 'en',
  year,
}: SearchParams): Promise<{ results: SubtitleSearchResult[]; notice?: string }> {
  if (!query.trim()) {
    return { results: [] };
  }

  const baseUrl = getApiBaseUrl();
  const params = new URLSearchParams();
  params.set('query', query.trim());
  if (language) params.set('language', language);
  if (year) params.set('year', String(year).trim());

  const url = `${baseUrl}/api/subtitles/search?${params.toString()}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  const text = await res.text();
  if (text.trim().startsWith('<')) {
    throw new Error('Received HTML response instead of JSON. Ensure backend signaling server is running on port 8787.');
  }

  let data: any;
  try {
    data = JSON.parse(text);
  } catch (err: any) {
    throw new Error(`Failed to parse subtitle response: ${err.message}`);
  }

  if (!res.ok) {
    throw new Error(data.error || `Subtitle search failed with status ${res.status}`);
  }

  return {
    results: data.data || [],
    notice: data.notice,
  };
}

/**
 * Downloads a subtitle file through the backend proxy.
 * Returns the raw subtitle text (.srt or .vtt).
 */
export async function downloadSubtitle(fileId: number, fileName: string): Promise<string> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/subtitles/download`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fileId, fileName }),
  });

  const text = await res.text();

  if (!res.ok) {
    let errorMessage = `Subtitle download failed with status ${res.status}`;
    try {
      const errorJson = JSON.parse(text);
      if (errorJson.error) errorMessage = errorJson.error;
    } catch {
      if (text && !text.startsWith('<')) errorMessage = text;
    }
    throw new Error(errorMessage);
  }

  // Handle direct download link if Worker returned a fallback URL
  try {
    const json = JSON.parse(text);
    if (json.downloadUrl) {
      console.log('[OpenSubtitles] Fetching direct download URL from browser:', json.downloadUrl);
      const directRes = await fetch(json.downloadUrl);
      const directText = await directRes.text();
      if (!directText.trim().startsWith('<') && !directText.trim().startsWith('<!DOCTYPE')) {
        return directText;
      }
    }
    if (json.error) {
      throw new Error(json.error);
    }
  } catch (e: any) {
    if (e.message && !e.message.includes('JSON')) {
      throw e;
    }
  }

  if (text.trim().startsWith('<') || text.trim().startsWith('<!DOCTYPE')) {
    throw new Error('Received unexpected HTML error page instead of subtitle file.');
  }

  return text;
}

/**
 * Parses raw subtitle text (SRT or VTT) into structured SubtitleCue objects
 * suitable for playback display.
 */
export function parseSubtitleText(rawText: string, fileName: string, language: string): SavedSubtitle {
  const isVtt = fileName.toLowerCase().endsWith('.vtt') || rawText.trim().startsWith('WEBVTT');
  const vttContent = isVtt ? rawText : parseSRT(rawText);
  const cues = parseVTT(vttContent);

  return {
    id: nanoid(10),
    name: fileName,
    language,
    cues,
    rawContent: vttContent,
  };
}

